import React from 'react';
import { Edit2, Trash2, ChevronUp, ChevronDown, Video, Users } from 'lucide-react';

// ==================== Team Member Card ====================

export const TeamMemberCard = ({ 
  member, 
  index, 
  totalInCategory,
  onEdit, 
  onDelete, 
  onMove 
}) => {
  const getBioPreview = (bio) => {
    if (typeof bio === 'string') return bio;
    return bio?.en || bio?.ko || bio?.ja || '';
  };

  // Get video URL (handle both old and new schema)
  const videoUrl = member.video_url || member.video || '';

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4"
      data-testid={`team-member-card-${member.id}`}
    >
      {/* Order Controls */}
      <div className="flex flex-col gap-1">
        <button
          onClick={() => onMove(member.id, 'up')}
          disabled={index === 0}
          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
          title="Move up"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <span className="text-xs text-center text-slate-400">{member.order}</span>
        <button
          onClick={() => onMove(member.id, 'down')}
          disabled={index === totalInCategory - 1}
          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
          title="Move down"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Avatar Placeholder */}
      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
        <Users className="w-6 h-6 text-slate-400" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-900">{member.name}</h3>
          {videoUrl && (
            <Video className="w-4 h-4 text-blue-500" title="Has video" />
          )}
        </div>
        <p className="text-sm text-slate-500">{member.role}</p>
        {getBioPreview(member.bio) && (
          <p className="text-xs text-slate-400 truncate mt-1">
            {getBioPreview(member.bio).substring(0, 60)}
            {getBioPreview(member.bio).length > 60 ? '...' : ''}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(member)}
          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title="Edit"
          data-testid={`edit-member-${member.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(member.id)}
          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
          data-testid={`delete-member-${member.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ==================== Team List Section ====================

export const TeamListSection = ({ 
  title, 
  members, 
  onEdit, 
  onDelete, 
  onMove,
  emptyMessage = 'No team members yet'
}) => {
  if (members.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      {members.map((member, index) => (
        <TeamMemberCard
          key={member.id}
          member={member}
          index={index}
          totalInCategory={members.length}
          onEdit={onEdit}
          onDelete={onDelete}
          onMove={onMove}
        />
      ))}
    </div>
  );
};

export default TeamListSection;
